import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "@user/components/Header";
import VerifiedAdvocateBadge from "@user/components/VerifiedAdvocateBadge";
import { api } from "@/lib/api";
import { uploadConsultationDocument } from "@shared/consultation/ConsultationDocuments";
import { BookingDocumentsPicker } from "@user/components/BookingDocumentsPicker";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, PhoneCall, Video, ShieldCheck, Lock } from "lucide-react";
import { SlotPicker, HoldCountdown } from "@user/components/SlotPicker";

const TYPES = [
  { key: "chat", label: "Chat", Icon: MessageSquare, desc: "Text messages with lawyer", levelDesc: "Text messages with lawyer", mult: 1.0 },
  { key: "audio", label: "Audio Call", Icon: PhoneCall, desc: "Voice call (20 min)", levelDesc: "Voice call", mult: 1.2 },
  { key: "video", label: "Video Call", Icon: Video, desc: "Face-to-face (30 min)", levelDesc: "Face-to-face video", mult: 1.5 },
];

const loadRzp = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Booking() {
  const { lawyerId } = useParams();
  const [sp] = useSearchParams();
  const initialType = sp.get("type") || "chat";
  const isInstant = sp.get("mode") === "instant";
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [lawyer, setLawyer] = useState(null);
  const [type, setType] = useState(initialType);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [query, setQuery] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [duration, setDuration] = useState(15);
  const [quote, setQuote] = useState(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [slotsRefresh, setSlotsRefresh] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) login();
  }, [authLoading, user, login]);

  useEffect(() => {
    api.get(`/lawyers/${lawyerId}`).then((r) => setLawyer(r.data)).catch(() => setLawyer(null));
  }, [lawyerId]);

  // Level-priced lawyers: server-computed quote (integer paise, rounded-up rupee)
  useEffect(() => {
    if (!lawyer?.has_level_pricing) { setQuote(null); return; }
    api.get(`/lawyers/${lawyerId}/quote`, { params: { mode: type, duration } })
      .then((r) => setQuote(r.data?.has_level_pricing ? r.data : null))
      .catch(() => setQuote(null));
  }, [lawyer, lawyerId, type, duration]);

  if (!lawyer || !user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-2xl mx-auto p-8 text-center text-slate-500">Loading…</div>
      </div>
    );
  }

  const selectedType = TYPES.find((t) => t.key === type);
  // Pricing comes pre-computed from backend. Falls back to legacy fee math
  // if the lawyer doc somehow lacks `pricing` (shouldn't happen post-migration).
  const typePricing =
    lawyer.pricing?.[type] ??
    (() => {
      const original = Math.round(lawyer.fee * selectedType.mult);
      return { original, offer: original, save_amount: 0, save_pct: 0, applied: "none" };
    })();
  const amount = typePricing.offer;
  const originalAmount = typePricing.original;
  const youSave = typePricing.save_amount;
  const isFirstOffer = typePricing.applied === "first" || !!lawyer.is_first_consultation;
  const leveled = !!lawyer.has_level_pricing && !!quote;
  const platformFee = leveled ? 0 : Math.round(amount * 0.05);
  const total = leveled ? quote.charged_paise / 100 : amount + platformFee;

  const book = async () => {
    if (!query.trim()) return toast.error("Please describe your legal issue");
    if (!isInstant && !slot) return toast.error("Please select a time slot");
    if (isInstant && !lawyer.instant_available) {
      toast.error("This lawyer just went offline. Please book a slot instead.");
      navigate(`/book/${lawyer.id}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        lawyer_id: lawyer.id,
        query,
        consultation_type: type,
        booking_type: isInstant ? "instant" : "scheduled",
      };
      if (lawyer.has_level_pricing) payload.duration_minutes = duration;
      if (!isInstant) {
        payload.slot_date = date;
        payload.slot_time = slot;
      }
      let createRes;
      try {
        createRes = await api.post("/consultations", payload);
      } catch (e) {
        if (e.response?.status === 409 && !isInstant) {
          // Someone else took the cell between display and Pay — refresh real slots inline.
          toast.error(e.response?.data?.detail || "Slot just taken — please pick another time.");
          setSlot(""); setSlotsRefresh((n) => n + 1); setBusy(false);
          return;
        }
        throw e;
      }
      const cid = createRes.data.id;
      if (createRes.data.hold_expires_at) setHoldExpiresAt(createRes.data.hold_expires_at);
      // Booking documents ride along before payment so the lawyer can read them before starting.
      for (const f of files) {
        try { await uploadConsultationDocument(cid, f, "booking"); }
        catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`); }
      }

      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load payment gateway"); setBusy(false); return; }

      const orderRes = await api.post(`/consultations/${cid}/order`);
      const { order_id, amount: amountPaise, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amountPaise,
        currency,
        order_id,
        name: "Advoset",
        description: `${selectedType.label} with ${lawyer.name}`,
        theme: { color: "#0A2540" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            const verifyRes = await api.post(`/consultations/${cid}/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            // A/B conversion tracking
            const session = localStorage.getItem("adv_exp_session") || "";
            const priceVariant = localStorage.getItem("adv_exp_chat_entry_price");
            if (priceVariant) {
              api.post("/experiments/event", { key: "chat_entry_price", variant: priceVariant, event: "conversion", session }).catch(() => {});
            }
            const ctaVariant = localStorage.getItem("adv_exp_cta_copy");
            if (ctaVariant) {
              api.post("/experiments/event", { key: "cta_copy", variant: ctaVariant, event: "conversion", session }).catch(() => {});
            }
            const headlineVariant = localStorage.getItem("adv_exp_hero_headline");
            if (headlineVariant) {
              api.post("/experiments/event", { key: "hero_headline", variant: headlineVariant, event: "conversion", session }).catch(() => {});
            }
            toast.success("Payment successful!");
            navigate(`/consultations/${cid}?invoice=${verifyRes.data.invoice_no}`);
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => { setBusy(false); setHoldExpiresAt(null); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 grid w-full grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: form */}
        <div className="min-w-0 lg:col-span-3 space-y-4">
          <div>
            <span className="adv-chip">{isInstant ? "Instant Consultation" : "Book a Consultation"}</span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-[#0A2540] break-words" style={{ fontFamily: "Poppins" }}>
              Talk to {lawyer.name.split(" ").slice(1).join(" ")}
            </h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <p className="text-sm text-slate-500">{lawyer.specialization} · {lawyer.city}</p>
              <VerifiedAdvocateBadge lawyer={lawyer} size="sm" showLabel />
            </div>
          </div>

          {isInstant && (
            <div data-testid="instant-banner" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <span className="h-9 w-9 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-emerald-900">Available now · you'll start the call right after payment</p>
                <p className="text-xs text-emerald-800/80">Skip slot selection — connect immediately with {lawyer.name.split(" ").slice(0,2).join(" ")}.</p>
              </div>
            </div>
          )}

          {/* Type */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">1 · Select consultation type</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  data-testid={`type-${t.key}`}
                  className={`rounded-xl border p-4 text-left transition-all ${type === t.key ? "border-[#0A2540] bg-[#0A2540] text-white shadow-lg" : "border-slate-200 bg-white hover:border-[#0A2540]"}`}
                >
                  <t.Icon size={20} className={type === t.key ? "text-[#D4AF37]" : "text-[#0A2540]"} />
                  <p className="mt-2 font-bold text-sm">{t.label}</p>
                  <p className={`text-xs ${type === t.key ? "text-white/70" : "text-slate-500"}`}>{lawyer.has_level_pricing ? t.levelDesc : t.desc}</p>
                  <p className={`mt-1 text-xs font-bold ${type === t.key ? "text-[#D4AF37]" : "text-[#0A2540]"}`}>₹{lawyer.pricing?.[t.key]?.offer ?? Math.round((lawyer.fee || 500) * t.mult)}</p>
                </button>
              ))}
            </div>
            {lawyer.has_level_pricing && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">Duration</p>
                <div className="flex gap-2">
                  {[15, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      data-testid={`duration-${d}`}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${duration === d ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-700 hover:border-[#0A2540]"}`}
                    >
                      {d} min{d === 30 ? " · 2×" : ""}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">Prices shown on the cards are for 15 min — your total below reflects the selected duration.</p>
              </div>
            )}
          </div>

          {/* Date + Slot — skipped for instant consultations */}
          {!isInstant && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">2 · Pick a time slot</p>
            <SlotPicker lawyerId={lawyer.id} duration={duration} date={date} slot={slot} refreshKey={slotsRefresh}
              onChange={(d, s) => { setDate(d); setSlot(s); }} />
          </div>
          )}

          {/* Query */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">{isInstant ? "2" : "3"} · Describe your issue</p>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Briefly describe your legal matter…"
              data-testid="booking-query"
              className="min-h-[100px]"
            />
            <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1"><Lock size={12} /> Only the lawyer will see this.</p>
            <BookingDocumentsPicker files={files} onChange={setFiles} />
          </div>
        </div>

        {/* RIGHT: summary */}
        <div className="min-w-0 lg:col-span-2">
          <div className="lg:sticky lg:top-20 bg-white border border-slate-200 rounded-2xl p-3 sm:p-5 space-y-2 sm:space-y-3">
            <h3 className="font-bold text-[#0A2540] text-sm sm:text-base" style={{ fontFamily: "Poppins" }}>Order summary</h3>
            <p className="text-[11px] sm:text-xs text-slate-500 -mt-1.5">{selectedType.label} consultation · {duration} minutes</p>

            {leveled ? (
              <>
                {/* Consultation price */}
                <div className="flex justify-between items-baseline gap-2" data-testid="summary-consultation-price">
                  <span className="text-xs sm:text-sm text-slate-500">Consultation price</span>
                  <span className="inline-flex items-baseline gap-2">
                    {(() => {
                      const mrp = quote.market_fee_rupees;
                      const price = quote.customer_price_before_discount_rupees ?? quote.customer_price_ex_gst_paise / 100;
                      const showMrp = mrp && mrp > 0 && mrp > price;
                      return (
                        <>
                          {showMrp && (
                            <span className="text-xs sm:text-sm text-slate-400 line-through font-medium tabular-nums">
                              ₹{mrp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          )}
                          <span className="text-sm sm:text-base font-bold text-[#0A2540] tabular-nums" data-testid="summary-before-discount">
                            ₹{price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </>
                      );
                    })()}
                  </span>
                </div>

                {/* Extra Discount */}
                {quote.platform_discount_paise > 0 && (
                  <div className="flex justify-between items-center text-xs sm:text-sm" data-testid="summary-extra-discount">
                    <span className="text-slate-600">Extra Discount ({(quote.platform_discount_pct_bps / 100).toFixed(0)}%)</span>
                    <span className="font-bold text-emerald-700 tabular-nums">−₹{(quote.platform_discount_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {/* Promotional / first offer */}
                {quote.discount_paise > 0 && (
                  <div className="flex justify-between items-center text-xs sm:text-sm" data-testid="summary-promo-discount">
                    <span className="text-slate-600">{quote.discount_applied === "first" ? "First Consultation Offer" : "Promotional Discount"} ({quote.discount_pct}% off)</span>
                    <span className="font-bold text-emerald-700 tabular-nums">−₹{(quote.discount_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}

                {/* Discounted price */}
                <div className="flex justify-between items-baseline text-xs sm:text-sm" data-testid="summary-discounted-price">
                  <span className="text-slate-500">Discounted price<span className="block text-[10px] text-slate-400">Before GST</span></span>
                  <span className="font-bold text-[#0A2540] tabular-nums">
                    ₹{(quote.customer_price_post_discount_rupees ?? quote.customer_price_ex_gst_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* GST */}
                <div className="flex justify-between items-baseline text-xs sm:text-sm" data-testid="summary-gst">
                  <span className="text-slate-500">GST ({(quote.gst_rate_bps || 1800) / 100}%)</span>
                  <span className="font-bold text-[#0A2540] tabular-nums">₹{(quote.gst_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                {/* Total payable */}
                <div className="h-px bg-slate-100 my-1" />
                <div className="flex justify-between items-baseline" data-testid="summary-total">
                  <span className="text-xs sm:text-sm font-bold text-[#0A2540]">Total payable</span>
                  <span className="text-xl sm:text-2xl font-extrabold text-[#0A2540] tabular-nums" style={{ fontFamily: "Poppins" }}>
                    ₹{(quote.charged_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* Savings message */}
                {(() => {
                  const mrp = quote.market_fee_rupees;
                  const gstRate = (quote.gst_rate_bps || 1800) / 10000;
                  const total = quote.charged_paise / 100;
                  if (!mrp || mrp <= 0) return null;
                  const referenceWithGst = Math.round(mrp * (1 + gstRate) * 100) / 100;
                  const savings = Math.round((referenceWithGst - total) * 100) / 100;
                  if (savings <= 0) return null;
                  return (
                    <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 flex items-start gap-2" data-testid="summary-savings">
                      <span className="text-emerald-600 text-sm">🎉</span>
                      <div>
                        <p className="text-xs sm:text-sm font-bold text-emerald-700">You're saving ₹{savings.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}!</p>
                        <p className="text-[9px] sm:text-[10px] text-emerald-600/80">*Savings compared with the reference consultation fee, including GST.</p>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="flex justify-between text-xs sm:text-sm items-baseline">
                  <span className="text-slate-500">{selectedType.label} · {lawyer.name}</span>
                  <span className="inline-flex items-baseline gap-2">
                    {youSave > 0 && (
                      <span className="text-xs text-slate-400 line-through" data-testid="summary-original">₹{originalAmount}</span>
                    )}
                    <span className="font-bold text-[#0A2540]" data-testid="summary-amount">₹{amount}</span>
                  </span>
                </div>
                {youSave > 0 && (
                  <div className="flex items-center gap-2 -mt-1" data-testid="summary-save">
                    {isFirstOffer ? (
                      <span data-testid="summary-first-offer-badge" className="text-[10px] font-bold uppercase tracking-wider bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5] rounded-full px-2 py-0.5">
                        🎉 First Consultation Offer · You save ₹{youSave}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
                        You save ₹{youSave} · {typePricing.save_pct}% OFF
                      </span>
                    )}
                  </div>
                )}
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-500">Platform fee (5%)</span>
                  <span className="font-bold text-[#0A2540]">₹{platformFee}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-500">GST</span>
                  <span className="font-bold text-[#0A2540]">Included</span>
                </div>

                <div className="h-px bg-slate-100 my-1" />
                <div className="flex justify-between items-baseline" data-testid="summary-total">
                  <span className="text-xs sm:text-sm font-bold text-[#0A2540]">Total payable</span>
                  <span className="text-xl sm:text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>₹{total}</span>
                </div>
              </>
            )}
            {busy && holdExpiresAt && <HoldCountdown expiresAt={holdExpiresAt} />}
            <Button
              onClick={book}
              disabled={busy}
              data-testid="confirm-booking-btn"
              className="w-full rounded-xl adv-btn-gold font-bold h-11"
            >
              {busy ? "Processing…" : isInstant ? `Pay ₹${total} & Start Now` : `Confirm Booking · ₹${total}`}
            </Button>
            <div className="flex items-center justify-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-600" /> Secure Razorpay</span>
              <span className="inline-flex items-center gap-1"><Lock size={12} className="text-[#D4AF37]" /> 100% private</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
